/**
 * CRM 360 — a **única** visão 360 do cliente (Task 13, brief 3.4).
 *
 * Esta tela substituiu a visão 360 parcial da Task 10 (que vivia em
 * `pages/Customer360.ts`). Lá, três coisas estavam declaradas como pendentes no
 * próprio código: o estágio de lead era estimado por faixa de score, as `tags`
 * eram sempre vazias e os pedidos só podiam ser vinculados por telefone. Esta
 * task fecha as três — e o faz **na mesma tela**, de propósito: duas telas de
 * 360 mostrariam dois totais para o mesmo cliente, que é exatamente o que a
 * regra "mesmos registros, mesmo histórico" proíbe (D-13.1).
 *
 * O que a tela reúne, sempre com link para o registro-fonte:
 *  - **cadastro unificado**: o cliente CANÔNICO (`customers` +
 *    `customer_identities`, 0020) e os seus identificadores por canal;
 *  - **empresa e contatos** (`companies`/`company_members`, 0018);
 *  - **estágio comercial e responsável** (oportunidade aberta, 0032);
 *  - **histórico de compras** multicanal (`platform_orders`, 0031);
 *  - **conversas** do atendimento;
 *  - **orçamentos** com total e margem recalculados dos itens;
 *  - **tarefas/follow-ups** com prazo e dono;
 *  - **segmentos** avaliados por regra salva;
 *  - **auditoria** do event store.
 *
 * Honestidade de dado nesta tela (o que NÃO é zero):
 *  - cada bloco cujo contrato não está publicado no ambiente declara isso
 *    (`awaiting_source`) em vez de aparecer vazio;
 *  - sem a fonte unificada de pedidos, o histórico de compras cai no checkout
 *    da Loja e **diz** que outros canais ficam fora;
 *  - segmento cuja regra depende de uma fonte ausente aparece como "não
 *    avaliável", não como "não pertence".
 */
/*
 * Redesign R6 (apresentação apenas): cada bloco do 360 vira um cartão
 * tokenizado no padrão aprovado (`rounded-panel border border-border bg-surface
 * p-4 shadow-s1`) com título `text-lg font-extrabold text-ink`; o resumo em
 * `<dl>` migra para grid tokenizado (`grid grid-cols-[max-content_1fr]`); notas
 * `.page__note` recebem `text-sm text-muted`. Todos os `data-*`, os links para
 * registros-fonte e os textos são PRESERVADOS — as classes hook seguem no markup.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide por bloco (`SectionTitle`, mesmo
 * componente local de `PedidoDetalhe.tsx`/`LeadScore.tsx`) e no título
 * principal, no mesmo padrão visual de `KpiCard.tsx` (ícone em caixa colorida
 * ao lado do título) — esta tela não tinha nenhum ícone antes, e é a maior do
 * escopo (9 blocos com a mesma estrutura de `PedidoDetalhe.tsx`, que já
 * estabeleceu este padrão). Puramente decorativo — nenhum `data-*`/texto/rota
 * muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { useState, type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Avatar, Badge, Tabs, buttonClass } from "../ui/index.js";
import {
  ArrowLeft,
  FileText,
  History,
  IdCard,
  ListChecks,
  MessageCircle,
  ShieldCheck,
  ShoppingCart,
  Tag,
  Target,
  type LucideIcon
} from "lucide-react";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { OrdersBoundary } from "../components/OrdersBoundary.js";
import { CrmBoundary } from "../components/CrmBoundary.js";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { OpportunityStage } from "../components/OpportunityStage.js";
import { buildCrm360, type Crm360TaskRow, type Crm360View } from "../crm/customer360.js";
import {
  LEAD_STAGE_LABELS,
  OPPORTUNITY_SOURCE_LABELS,
  QUOTE_STATUS_LABELS,
  TASK_KIND_LABELS
} from "../crm/pipeline.js";
import { ORDER_ORIGIN_LABELS, ORDER_STATE_LABELS } from "../orders/state-machine.js";
import { useAuditTrail, useCrm360, useCrmDataset, useOpportunityStage } from "../queries.js";
import { AWAITING_CONTRACT_LABEL, type DashboardDataset } from "../dashboard/kpis.js";
import { formatBrl, formatChannel, formatCount, formatDateTime, formatOrderStatus, formatPercent } from "../format.js";
import type { QuoteView } from "../crm/dataset.js";
import type { CrmDataset } from "../crm/dataset.js";
import type { Crm360Order } from "../crm/contract.js";
import type { OrdersDataset } from "../orders/dataset.js";
import type { Crm360ViewProps, OpportunityStageHandle } from "../crm/view-props.js";
import type { AuditLogEntry } from "../../../../packages/ui/src/types.js";
import type {
  CrmCompanyContact,
  CrmCustomerIdentity,
  CrmOpportunity,
  PlatformConversation,
  PlatformOrder
} from "../api/types.js";

/** Bloco cuja fonte não respondeu: declarado, nunca exibido como vazio. */
function awaitingBlock(title: string, detail: string): ReactElement {
  return <StatusPanel kind="awaiting_source" title={`${title} ${AWAITING_CONTRACT_LABEL}`} detail={detail} />;
}

/**
 * Título de bloco com ícone em caixa colorida — mesmo padrão visual do
 * `kpi-card__icon` em `KpiCard.tsx`/`SectionTitle` de `PedidoDetalhe.tsx`
 * (ícone decorativo, o nome acessível do bloco continua sendo o texto do `h3`).
 */
function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-wash text-brand-text">
        <Icon className="h-4 w-4" aria-hidden focusable={false} />
      </span>
      <h3 className="text-lg font-extrabold text-ink">{children}</h3>
    </div>
  );
}

function CadastroBlock({ view }: { view: Crm360View }): ReactElement {
  const { customer, canonical, company } = view;
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Cadastro unificado do cliente"
      data-section="cadastro"
    >
      <SectionTitle icon={IdCard}>Cadastro unificado</SectionTitle>
      {canonical ? (
        <dl className="crm360__summary m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-control border border-border bg-surface-soft p-3 text-sm [&_dt]:text-muted [&_dd]:m-0 [&_dd]:text-ink">
          <dt>Cliente canônico</dt>
          <dd data-field="canonicalId">{canonical.id}</dd>
          <dt>Origem do cadastro</dt>
          <dd data-field="origin">{canonical.origin}</dd>
          <dt>Telefone</dt>
          <dd data-field="phone">{canonical.phone || "não informado"}</dd>
          <dt>E-mail</dt>
          <dd data-field="email">{canonical.email ?? "não informado"}</dd>
          <dt>Situação</dt>
          <dd data-field="status">{canonical.status}</dd>
        </dl>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Cliente sem cadastro canônico resolvido"
          detail={`O identificador ${customer.customerId} não resolveu para um registro de customers (migration 0020). Os blocos abaixo mostram o que pôde ser vinculado pela identidade do canal — não significam 'cliente novo'.`}
        />
      )}
      {canonical ? (
        <RecordTable<CrmCustomerIdentity>
          bare
        caption="Identificadores do cliente por canal (customer_identities, 0020)"
          rows={canonical.identities}
          idOf={(identity) => `${identity.kind}:${identity.value}`}
          emptyMessage="Nenhum identificador de canal registrado para este cliente."
          columns={[
            { key: "kind", label: "Tipo", render: (identity) => identity.kind },
            { key: "value", label: "Identificador", render: (identity) => identity.value },
            {
              key: "verified",
              label: "Verificado",
              render: (identity) => (
                <span data-identity-verified={identity.verified ? "true" : "false"}>{identity.verified ? "sim" : "não comprovado"}</span>
              )
            },
            { key: "source", label: "Origem", render: (identity) => identity.source }
          ]}
        />
      ) : null}
      {company ? (
        <div className="crm360__company flex flex-col gap-2" data-company-id={company.id}>
          <h4 className="text-base font-extrabold text-ink">{`Empresa: ${company.tradeName ?? company.legalName}`}</h4>
          <p className="text-sm text-body" data-company-status={company.status}>
            {`Razão social: ${company.legalName} · CNPJ: ${company.taxId ?? "não informado"} · situação: ${company.status}`}
          </p>
          <RecordTable<CrmCompanyContact>
            bare
        caption="Contatos da empresa (company_members, 0018)"
            rows={company.contacts}
            idOf={(contact) => contact.profileId}
            emptyMessage="Nenhum contato vinculado a esta empresa."
            columns={[
              { key: "name", label: "Contato", render: (contact) => contact.name },
              { key: "email", label: "E-mail", render: (contact) => contact.email ?? "não informado" },
              { key: "role", label: "Papel", render: (contact) => (contact.companyRole === "owner" ? "responsável" : "membro") }
            ]}
          />
        </div>
      ) : (
        <p className="page__note text-sm text-muted" data-company-id="none">
          {view.crmSources.companies === "awaiting_contract"
            ? `Empresas ${AWAITING_CONTRACT_LABEL}: não é possível dizer se este cliente é B2B neste ambiente.`
            : "Cliente sem empresa vinculada (pessoa física). Empresas B2B vêm de companies (migration 0018)."}
        </p>
      )}
    </section>
  );
}

function SegmentosBlock({ view }: { view: Crm360View }): ReactElement {
  if (view.crmSources.segments === "awaiting_contract") {
    return (
      <section
        className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
        aria-label="Segmentos do cliente"
        data-section="segmentos"
      >
        <SectionTitle icon={Tag}>Segmentos</SectionTitle>
        {awaitingBlock(
          "Segmentação",
          "A rota /api/crm/segments não respondeu neste ambiente. Sem as regras salvas não há segmento a avaliar — lista vazia aqui diria, falsamente, que o cliente não pertence a nenhum."
        )}
      </section>
    );
  }
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Segmentos do cliente"
      data-section="segmentos"
    >
      <SectionTitle icon={Tag}>Segmentos</SectionTitle>
      <p className="page__note text-sm text-muted" data-segment-tags={view.customer.tags.join(",")}>
        {view.customer.tags.length ? `Pertence a: ${view.customer.tags.join(" · ")}` : "Nenhum segmento ativo casou com os registros deste cliente."}
      </p>
      <RecordTable<Crm360View["segments"][number]>
        bare
        caption="Avaliação de cada segmento ativo sobre os registros reais deste cliente"
        rows={view.segments}
        idOf={(evaluation) => evaluation.key}
        emptyMessage="Nenhum segmento ativo cadastrado."
        columns={[
          {
            key: "segment",
            label: "Segmento",
            render: (evaluation) => <Link to={`/crm/segmentos?segmento=${encodeURIComponent(evaluation.key)}`}>{evaluation.name}</Link>
          },
          {
            key: "match",
            label: "Resultado",
            render: (evaluation) => (
              <span data-segment-match={evaluation.match.state}>
                {evaluation.match.state === "match"
                  ? "pertence"
                  : evaluation.match.state === "no_match"
                    ? "não pertence"
                    : `não avaliável — ${evaluation.match.reason}`}
              </span>
            )
          },
          {
            key: "rule",
            label: "Regra",
            render: (evaluation) => (evaluation.ruleUnderstood ? "reconhecida" : "não reconhecida por esta versão")
          }
        ]}
      />
    </section>
  );
}

function ComprasBlock({ view }: { view: Crm360View }): ReactElement {
  const unified = view.purchaseSource === "unified";
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Histórico de compras do cliente"
      data-section="compras"
    >
      <SectionTitle icon={ShoppingCart}>Histórico de compras</SectionTitle>
      <p
        className="page__note text-sm text-muted"
        data-purchase-source={view.purchaseSource}
        data-order-link={view.orderLink}
        // Procedência do recorte: runtime persistente do CRM ou listas do CRM
        // filtradas na tela. Declarada porque as duas respostas têm de ser o
        // mesmo cliente — e uma divergência precisa ser visível.
        data-aggregation-source={view.aggregationSource}
      >
        {unified
          ? `Pedidos de todos os canais vinculados a este cliente (vínculo ${
              view.orderLink === "canonical" ? "pelo cadastro canônico" : "pelo telefone do canal"
            }). Total gasto conta apenas pedidos em estado de receita: cancelado e devolvido aparecem na lista e ficam fora do total.`
          : "Fonte unificada de pedidos não respondeu neste ambiente: a lista abaixo é apenas o checkout da Loja. Vendas do WhatsApp e de outros canais NÃO entram neste total."}
      </p>
      {view.orderLink === "unresolved" ? (
        <StatusPanel
          kind="awaiting_source"
          title="Pedidos ainda não vinculáveis a este cliente"
          detail="Não há cadastro canônico resolvido nem telefone no canal desta conversa, então nenhum pedido pôde ser vinculado. Os totais acima contam o que pôde ser vinculado — não significam 'cliente sem compras'."
        />
      ) : null}
      {unified ? (
        <RecordTable<Crm360Order>
          bare
        caption="Pedidos que compõem o histórico de compras deste cliente"
          rows={view.orders}
          idOf={(order) => order.id}
          emptyMessage="Nenhum pedido vinculado a este cliente na fila unificada."
          columns={[
            { key: "id", label: "Pedido", render: (order) => <Link to={`/pedidos/${order.id}`}>{order.id}</Link> },
            {
              key: "origin",
              label: "Canal",
              render: (order) => <span data-order-origin={order.origin}>{ORDER_ORIGIN_LABELS[order.origin]}</span>
            },
            { key: "externalId", label: "Identificador no canal", render: (order) => order.externalId },
            {
              key: "state",
              label: "Estado",
              render: (order) => <span data-order-state={order.state}>{ORDER_STATE_LABELS[order.state]}</span>
            },
            { key: "placedAt", label: "Data", render: (order) => formatDateTime(order.placedAt) },
            {
              key: "total",
              label: "Total",
              render: (order) => (order.totalCents === null ? "aguardando cotação de frete" : formatBrl(order.totalCents))
            },
            {
              key: "revenue",
              label: "Conta no total gasto",
              render: (order) => (
                <span data-counts-as-revenue={view.revenueOrderIds.includes(order.id) ? "true" : "false"}>
                  {view.revenueOrderIds.includes(order.id) ? "sim" : "não"}
                </span>
              )
            }
          ]}
        />
      ) : (
        <RecordTable<PlatformOrder>
          bare
        caption="Pedidos do checkout da Loja vinculados a este cliente (recorte de um canal)"
          rows={view.checkoutOrders}
          idOf={(order) => order.id}
          emptyMessage="Nenhum pedido do checkout vinculado a este cliente."
          columns={[
            { key: "id", label: "Pedido", render: (order) => order.id },
            { key: "placedAt", label: "Data", render: (order) => formatDateTime(order.placedAt) },
            { key: "status", label: "Status", render: (order) => formatOrderStatus(order.status) },
            {
              key: "total",
              label: "Total",
              render: (order) => (order.totalCents === null ? "aguardando cotação de frete" : formatBrl(order.totalCents))
            }
          ]}
        />
      )}
    </section>
  );
}

function ConversasBlock({ view }: { view: Crm360View }): ReactElement {
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Conversas do cliente"
      data-section="conversas"
    >
      <SectionTitle icon={MessageCircle}>Conversas</SectionTitle>
      <RecordTable<PlatformConversation>
        bare
        caption="Conversas que compõem esta visão 360"
        rows={view.conversations}
        idOf={(conversation) => conversation.id}
        emptyMessage="Nenhuma conversa registrada para este cliente."
        columns={[
          {
            key: "id",
            label: "Conversa",
            render: (conversation) => <Link to={`/inbox/${conversation.id}`}>{conversation.id}</Link>
          },
          { key: "channel", label: "Canal", render: (conversation) => formatChannel(conversation.channel) },
          { key: "mode", label: "Atendimento", render: (conversation) => (conversation.mode === "human" ? "humano" : "IA") },
          { key: "leadScore", label: "Lead score", render: (conversation) => formatCount(conversation.leadScore) },
          { key: "updatedAt", label: "Atualizada em", render: (conversation) => formatDateTime(conversation.updatedAt) }
        ]}
      />
    </section>
  );
}

function OrcamentosBlock({ view }: { view: Crm360View }): ReactElement {
  if (view.crmSources.quotes === "awaiting_contract") {
    return (
      <section
        className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
        aria-label="Orçamentos do cliente"
        data-section="orcamentos"
      >
        <SectionTitle icon={FileText}>Orçamentos</SectionTitle>
        {awaitingBlock(
          "Orçamentos",
          "A rota /api/crm/quotes não respondeu neste ambiente. Lista vazia aqui diria que nunca houve proposta para este cliente."
        )}
      </section>
    );
  }
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Orçamentos do cliente"
      data-section="orcamentos"
    >
      <SectionTitle icon={FileText}>Orçamentos</SectionTitle>
      <RecordTable<QuoteView>
        bare
        caption="Propostas comerciais deste cliente, com total e margem recalculados dos itens"
        rows={view.quotes}
        idOf={(item) => item.quote.id}
        emptyMessage="Nenhum orçamento emitido para este cliente."
        columns={[
          {
            key: "number",
            label: "Proposta",
            // O destino recebe o recorte do cliente E o id da proposta clicada:
            // sem `quote=`, "ver a proposta" abriria todas as propostas do
            // cliente e o operador perderia qual delas ele estava lendo.
            render: (item) => (
              <Link to={`/crm/orcamentos?customer=${encodeURIComponent(item.quote.customerId)}&quote=${encodeURIComponent(item.quote.id)}`}>
                {item.quote.number}
              </Link>
            )
          },
          {
            key: "status",
            label: "Situação",
            render: (item) => <span data-quote-status={item.status}>{QUOTE_STATUS_LABELS[item.status]}</span>
          },
          {
            key: "total",
            label: "Total",
            render: (item) =>
              item.totals.totalCents === null ? <span data-pending="true">sem frete cotado</span> : formatBrl(item.totals.totalCents)
          },
          {
            key: "margin",
            label: "Margem",
            render: (item) =>
              item.totals.marginPct === null ? <span data-pending="true">custo não sincronizado</span> : formatPercent(item.totals.marginPct)
          },
          {
            key: "validUntil",
            label: "Validade",
            render: (item) => (item.quote.validUntil ? formatDateTime(item.quote.validUntil) : "sem prazo declarado")
          },
          {
            key: "order",
            label: "Venda",
            render: (item) =>
              item.quote.platformOrderId ? <Link to={`/pedidos/${item.quote.platformOrderId}`}>ver pedido gerado</Link> : "ainda não virou pedido"
          }
        ]}
      />
    </section>
  );
}

function OportunidadesBlock({ view }: { view: Crm360View }): ReactElement {
  if (view.crmSources.opportunities === "awaiting_contract") {
    return (
      <section
        className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
        aria-label="Oportunidades do cliente"
        data-section="oportunidades"
      >
        <SectionTitle icon={Target}>Oportunidades</SectionTitle>
        {awaitingBlock(
          "Pipeline",
          "A rota /api/crm/opportunities não respondeu neste ambiente. O estágio comercial exibido no resumo é estimado pelo lead score enquanto o funil não responde."
        )}
      </section>
    );
  }
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Oportunidades do cliente"
      data-section="oportunidades"
    >
      <SectionTitle icon={Target}>Oportunidades</SectionTitle>
      <RecordTable<CrmOpportunity>
        bare
        caption="Negociações deste cliente, abertas e encerradas"
        rows={view.opportunities}
        idOf={(opportunity) => opportunity.id}
        emptyMessage="Nenhuma oportunidade aberta para este cliente."
        columns={[
          {
            key: "title",
            label: "Negociação",
            // Estágio (onde olhar) + id da negociação (qual registro abrir).
            render: (opportunity) => (
              <Link to={`/crm/pipeline?stage=${encodeURIComponent(opportunity.stage)}&opportunity=${encodeURIComponent(opportunity.id)}`}>
                {opportunity.title}
              </Link>
            )
          },
          {
            key: "stage",
            label: "Estágio",
            render: (opportunity) => <span data-opportunity-stage={opportunity.stage}>{LEAD_STAGE_LABELS[opportunity.stage]}</span>
          },
          { key: "owner", label: "Responsável", render: (opportunity) => opportunity.ownerLabel },
          {
            key: "value",
            label: "Valor",
            render: (opportunity) =>
              opportunity.valueCents === null ? <span data-pending="true">sem valor declarado</span> : formatBrl(opportunity.valueCents)
          },
          {
            key: "source",
            label: "Origem",
            render: (opportunity) => OPPORTUNITY_SOURCE_LABELS[opportunity.source]
          },
          {
            key: "closure",
            label: "Fechamento",
            render: (opportunity) =>
              opportunity.stage === "ganho" && opportunity.platformOrderId ? (
                <Link to={`/pedidos/${opportunity.platformOrderId}`}>ver pedido da venda</Link>
              ) : opportunity.stage === "perdido" ? (
                `perdido: ${opportunity.lostReason ?? "motivo não registrado"}`
              ) : (
                "em aberto"
              )
          }
        ]}
      />
    </section>
  );
}

function TarefasBlock({ view }: { view: Crm360View }): ReactElement {
  if (view.crmSources.tasks === "awaiting_contract") {
    return (
      <section
        className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
        aria-label="Tarefas do cliente"
        data-section="tarefas"
      >
        <SectionTitle icon={ListChecks}>Tarefas e follow-ups</SectionTitle>
        {awaitingBlock(
          "Tarefas",
          "A rota /api/crm/tasks não respondeu neste ambiente. Lista vazia aqui diria que não há nada a fazer com este cliente."
        )}
      </section>
    );
  }
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Tarefas do cliente"
      data-section="tarefas"
    >
      <SectionTitle icon={ListChecks}>Tarefas e follow-ups</SectionTitle>
      <RecordTable<Crm360TaskRow>
        bare
        caption="Tarefas comerciais deste cliente, em ordem de prazo"
        rows={view.tasks}
        idOf={(row) => row.task.id}
        emptyMessage="Nenhuma tarefa registrada para este cliente."
        columns={[
          {
            key: "title",
            label: "Tarefa",
            // Fila do responsável + id da tarefa: a fila é o contexto, o id é o
            // registro que o operador clicou.
            render: (row) => (
              <Link to={`/crm/tarefas?owner=${encodeURIComponent(row.task.ownerProfileId)}&task=${encodeURIComponent(row.task.id)}`}>
                {row.task.title}
              </Link>
            )
          },
          { key: "kind", label: "Tipo", render: (row) => TASK_KIND_LABELS[row.task.kind] },
          { key: "owner", label: "Responsável", render: (row) => row.task.ownerLabel },
          { key: "dueAt", label: "Prazo", render: (row) => formatDateTime(row.task.dueAt) },
          {
            key: "due",
            label: "Situação do prazo",
            render: (row) => (
              <span data-task-due={row.due}>
                {row.due === "overdue" ? "atrasada" : row.due === "today" ? "vence hoje" : row.due === "upcoming" ? "a vencer" : "encerrada"}
              </span>
            )
          },
          {
            key: "status",
            label: "Resultado",
            render: (row) => (row.task.status === "open" ? "em aberto" : `${row.task.status}: ${row.task.outcome || "sem registro"}`)
          }
        ]}
      />
    </section>
  );
}

/**
 * Dado pessoal do titular (LGPD) — montado por `packages/admin-ops`
 * (`buildCustomerDataExport`), as mesmas operações de `/api/admin/lgpd/*`.
 *
 * A tela mostra O QUE o pacote de portabilidade carrega (contagem por fonte,
 * vinda da MESMA agregação exibida acima) em vez de prometer um arquivo que
 * esta tela não emite: a execução do pedido é do runtime, que registra em
 * `lgpd_requests`.
 */
function DadosPessoaisBlock({ view }: { view: Crm360View }): ReactElement {
  const summary = view.personalData;
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Dados pessoais do titular (LGPD)"
      data-section="lgpd"
    >
      <SectionTitle icon={ShieldCheck}>Dados pessoais do titular (LGPD)</SectionTitle>
      {summary ? (
        <dl
          className="crm360__summary m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-control border border-border bg-surface-soft p-3 text-sm [&_dt]:text-muted [&_dd]:m-0 [&_dd]:text-ink"
          data-lgpd-export={summary.customerId}
          data-lgpd-anonymized={summary.anonymized ? "true" : "false"}
        >
          <dt>Conversas no pacote</dt>
          <dd data-lgpd-field="conversations">{formatCount(summary.conversations)}</dd>
          <dt>Pedidos no pacote</dt>
          <dd data-lgpd-field="orders">{formatCount(summary.orders)}</dd>
          <dt>Negociações no pacote</dt>
          <dd data-lgpd-field="opportunities">{formatCount(summary.opportunities)}</dd>
          <dt>Propostas no pacote</dt>
          <dd data-lgpd-field="quotes">{formatCount(summary.quotes)}</dd>
          <dt>Situação do cadastro</dt>
          <dd data-lgpd-field="status">
            {summary.anonymized
              ? "anonimizado a pedido do titular — nome, telefone, e-mail e identificadores de canal não são exibidos"
              : "ativo: o pacote de portabilidade reúne os registros acima"}
          </dd>
        </dl>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Sem titular resolvido para exportar"
          detail="Este identificador não resolveu para um cadastro canônico (customers, 0020), então não há pacote de portabilidade a montar — o que aparece acima é o que pôde ser vinculado pela identidade do canal."
        />
      )}
    </section>
  );
}

function AuditoriaBlock({ view, auditUnavailableReason }: { view: Crm360View; auditUnavailableReason?: string }): ReactElement {
  return (
    <section
      className="crm360__block flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
      aria-label="Auditoria do cliente"
      data-section="auditoria"
    >
      <SectionTitle icon={History}>Audit log</SectionTitle>
      {view.auditAvailable ? (
        <RecordTable<AuditLogEntry>
          bare
        caption="Eventos auditáveis ligados a este cliente, suas conversas, pedidos e oportunidades"
          rows={view.customer.auditLog}
          idOf={(entry) => entry.id}
          emptyMessage="Nenhum evento auditável registrado para este cliente ainda."
          columns={[
            { key: "occurredAt", label: "Quando", render: (entry) => formatDateTime(entry.occurredAt) },
            { key: "actor", label: "Autor", render: (entry) => `${entry.actorLabel} (${entry.actorType})` },
            { key: "action", label: "Ação", render: (entry) => entry.action },
            { key: "details", label: "Registro", render: (entry) => entry.details }
          ]}
        />
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Trilha de auditoria indisponível"
          detail={
            auditUnavailableReason ??
            "A rota /api/admin/audit não está publicada neste ambiente. Lista vazia aqui diria, falsamente, que nada aconteceu com este cliente."
          }
        />
      )}
    </section>
  );
}

export function Crm360View({ dataset, customerId, auditEvents, auditUnavailableReason, orders, crm, crm360, now, stage }: Crm360ViewProps): ReactElement {
  const [tab, setTab] = useState<Crm360Tab>("visao");
  const view = buildCrm360({ dataset, customerId, auditEvents, orders, crm, crm360, now });
  if (!view) {
    // "Não encontrei" e "não consegui verificar" são coisas diferentes. Se
    // alguma fonte que poderia conter este cliente não respondeu, dizer
    // "cliente sem registro" seria a mesma mentira que um total zerado por
    // fonte ausente — então declaramos quais fontes faltaram.
    const unavailable = [
      ...(crm
        ? (Object.keys(crm.sources) as Array<keyof CrmDataset["sources"]>)
            .filter((source) => crm.sources[source] === "awaiting_contract")
            .map((source) => `/api/crm/${source}`)
        : ["/api/crm/* (CRM não carregado nesta montagem)"]),
      ...(!orders || orders.sources.platformOrders === "awaiting_contract" ? ["/api/platform/orders"] : [])
    ];
    return (
      <div className="page page--crm360 flex flex-col gap-6" data-page="crm-360">
        <h2 className="text-2xl font-black text-ink sm:text-3xl">CRM 360</h2>
        {unavailable.length ? (
          <StatusPanel
            kind="awaiting_source"
            title={`Não foi possível verificar os registros de ${customerId}`}
            detail={`Nenhum registro encontrado nas fontes que responderam, mas estas não responderam neste ambiente: ${unavailable.join(", ")}. Dizer "cliente sem registro" aqui afirmaria algo que não foi verificado.`}
          />
        ) : (
          <StatusPanel
            kind="empty"
            title="Cliente sem registro na Plataforma"
            detail={`Nenhuma conversa, cadastro canônico, pedido ou negociação para ${customerId}. Todas as fontes responderam — a visão 360 é montada a partir dos registros reais e não há cadastro a exibir.`}
          />
        )}
      </div>
    );
  }
  const { customer, activeOpportunity, leadStageEstimated } = view;
  const panel = (id: Crm360Tab, children: ReactNode): ReactElement => (
    <div key={id} role="tabpanel" aria-label={id} hidden={tab !== id} data-tab-panel={id} className="flex flex-col gap-6">
      {children}
    </div>
  );
  const openOpportunities = view.opportunities.filter((item) => item.stage !== "ganho" && item.stage !== "perdido").length;
  return (
    <div className="page page--crm360 flex flex-col gap-6" data-page="crm-360" data-customer-id={customer.customerId}>
      <Link to="/clientes" className="drilldown-header__back inline-flex w-fit items-center gap-1 text-sm font-bold text-muted hover:text-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden focusable={false} />
        Clientes
      </Link>
      {/* Cabeçalho do contato (modelo "pessoa" do Pipedrive): quem é, como falar, o que vale. */}
      <section aria-label="Resumo do cliente" className="flex flex-col gap-5 rounded-panel border border-border bg-surface p-5 shadow-s1">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar name={customer.name} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="text-2xl font-black text-ink sm:text-3xl">{`CRM 360 — ${customer.name}`}</h2>
            <div className="drilldown-header__origin flex flex-wrap items-center gap-2 text-sm text-muted">
              <Badge tone={leadStageEstimated ? "neutral" : "brand"} data-field="leadStage" data-estimated={leadStageEstimated ? "true" : "false"}>
                {LEAD_STAGE_LABELS[customer.leadStage]}
                {leadStageEstimated ? " (estimado pelo lead score)" : ""}
              </Badge>
              <span data-field="phone">{customer.phone || "telefone não informado"}</span>
              <span aria-hidden>·</span>
              <span data-field="email">{customer.email ?? "e-mail não informado"}</span>
            </div>
            <p className="m-0 text-xs text-muted">
              {`${customer.conversationIds.length} conversa(s), ${customer.totalOrders} pedido(s), ${view.quotes.length} orçamento(s) e ${view.opportunities.length} oportunidade(s) do MESMO cliente.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {customer.conversationIds[0] ? (
              <Link to={`/inbox/${customer.conversationIds[0]}`} className={buttonClass("outline", "md")}>
                Abrir conversa
              </Link>
            ) : (
              <Link to="/inbox" className="drilldown-header__back text-sm font-bold text-muted hover:text-ink">
                Voltar ao atendimento
              </Link>
            )}
            <Link to="/televendas" className={buttonClass("primary", "md")}>
              Novo pedido
            </Link>
          </div>
        </div>
        <dl className="crm360__summary m-0 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-border pt-4 sm:grid-cols-3 lg:grid-cols-5">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm font-extrabold text-muted">Total gasto</dt>
            <dd className="m-0 text-2xl font-black tabular-nums text-ink" data-field="totalSpentCents">{formatBrl(customer.totalSpentCents)}</dd>
            <span className="text-xs text-muted">Pedidos em estado de receita</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm font-extrabold text-muted">Pedidos</dt>
            <dd className="m-0 text-2xl font-black tabular-nums text-ink">{formatCount(customer.totalOrders)}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm font-extrabold text-muted">Negociações abertas</dt>
            <dd className="m-0 text-2xl font-black tabular-nums text-ink">{formatCount(openOpportunities)}</dd>
            <span className="text-xs text-muted" data-field="owner">{activeOpportunity ? activeOpportunity.ownerLabel : "nenhuma negociação em aberto"}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm font-extrabold text-muted">Lead score</dt>
            <dd className="m-0 text-2xl font-black tabular-nums text-ink" data-field="leadScore">{formatCount(customer.leadScore)}</dd>
            <Link to={`/clientes/${customer.customerId}/lead-score`} className="text-xs font-bold text-brand-text underline">
              ver composição do score
            </Link>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm font-extrabold text-muted">Última interação</dt>
            <dd className="m-0 text-base font-extrabold text-ink" data-field="lastInteractionAt">{formatDateTime(customer.lastInteractionAt)}</dd>
          </div>
        </dl>
      </section>
      <Tabs<Crm360Tab>
        label="Seções do cliente"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "visao", label: "Visão geral" },
          { id: "negocios", label: "Negócios", count: view.opportunities.length },
          { id: "compras", label: "Compras e orçamentos", count: customer.totalOrders + view.quotes.length },
          { id: "conversas", label: "Conversas", count: customer.conversationIds.length },
          { id: "cadastro", label: "Cadastro e LGPD" },
          { id: "auditoria", label: "Auditoria" }
        ]}
      />
      {panel(
        "visao",
        <>
          {activeOpportunity ? (
            <OpportunityStage opportunity={activeOpportunity} stage={stage} />
          ) : (
            <p
              className="page__note m-0 rounded-panel border border-border bg-surface p-4 text-sm text-muted shadow-s1"
              data-section="estagio-comercial"
            >
              {view.crmSources.opportunities === "awaiting_contract"
                ? `Estágio comercial ${AWAITING_CONTRACT_LABEL}: o funil não respondeu neste ambiente.`
                : "Nenhuma negociação em aberto para este cliente. O estágio exibido acima é estimativa pelo lead score."}
            </p>
          )}
          <TarefasBlock view={view} />
          <SegmentosBlock view={view} />
        </>
      )}
      {panel("negocios", <OportunidadesBlock view={view} />)}
      {panel(
        "compras",
        <>
          <ComprasBlock view={view} />
          <OrcamentosBlock view={view} />
        </>
      )}
      {panel("conversas", <ConversasBlock view={view} />)}
      {panel(
        "cadastro",
        <>
          <CadastroBlock view={view} />
          <DadosPessoaisBlock view={view} />
        </>
      )}
      {panel("auditoria", <AuditoriaBlock view={view} auditUnavailableReason={auditUnavailableReason} />)}
    </div>
  );
}

type Crm360Tab = "visao" | "negocios" | "compras" | "conversas" | "cadastro" | "auditoria";

export function Crm360(): ReactElement {
  const { customerId } = useParams<{ customerId: string }>();
  const audit = useAuditTrail();
  const crm = useCrmDataset();
  // Agregação canônica do runtime persistente: é ela que decide QUAIS registros
  // são deste cliente (o servidor resolve a identidade pela 0020). O id da rota
  // pode ser o cadastro canônico ou o identificador do canal — a rota aceita os
  // dois e devolve o MESMO cliente, que é o ponto da Task 13.
  const canonical = useCrm360({ customerId });
  const mutation = useOpportunityStage();
  if (!customerId) {
    return <StatusPanel kind="empty" title="Informe o cliente na rota /clientes/:customerId" />;
  }
  // Mesmo padrão das Tasks 11 e 12: a view recebe o caminho de gravação por
  // prop, então o teste exercita o MESMO submit sem navegador. O id da
  // oportunidade é resolvido no submit porque a oportunidade exibida é a aberta
  // mais recente, decidida pela regra pura e não pela tela.
  const stage: OpportunityStageHandle = {
    submit: (input) => {
      // As negociações do payload canônico já são do cliente resolvido no
      // servidor; sem o payload, caímos na lista e filtramos pelo id da rota.
      const opportunities =
        canonical.data?.opportunities ?? crm.data?.opportunities.filter((opportunity) => opportunity.customerId === customerId) ?? [];
      const opportunityId = opportunities.find((opportunity) => opportunity.stage !== "ganho" && opportunity.stage !== "perdido")?.id ?? "";
      if (!opportunityId) return;
      mutation.mutate({ opportunityId, input });
    },
    pending: mutation.isPending,
    error: mutation.error ?? null,
    savedStage: mutation.data?.stage ?? null
  };
  return (
    <DatasetBoundary>
      {(data: DashboardDataset) => (
        <OrdersBoundary>
          {(ordersDataset: OrdersDataset) => (
            <CrmBoundary>
              {(crmDataset: CrmDataset) =>
                audit.isPending || canonical.isPending ? (
                  <StatusPanel kind="loading" title="Carregando histórico do cliente…" />
                ) : (
                  <Crm360View
                    dataset={data}
                    customerId={customerId}
                    auditEvents={audit.error ? null : audit.data ?? null}
                    auditUnavailableReason={audit.error ? errorDetail(audit.error) : undefined}
                    orders={ordersDataset}
                    crm={crmDataset}
                    // Erro de verdade na rota canônica (5xx, payload fora do
                    // contrato) NÃO vira recorte das listas em silêncio: o
                    // payload fica ausente e a tela declara a procedência.
                    crm360={canonical.error ? null : canonical.data ?? null}
                    stage={stage}
                  />
                )
              }
            </CrmBoundary>
          )}
        </OrdersBoundary>
      )}
    </DatasetBoundary>
  );
}
